import { useData, usePlayerEngine } from "../../../../runtime/provider";
import { usePlayerStoreApi } from "@utils/player/core/usePlayer";
import { useAppSetupStore } from "@hooks/useAppSetup/useAppSetupStore";
import { incrementFairPlays } from "@hooks/profile/useUserStats";
import { useEffect } from "react";
import { Platform } from "react-native";
import { captureException } from "@utils/captureException";
import { getUser } from "@hooks/trpc/useMe";
import { getPlayDeviceId } from "./playDeviceId";

import {
  selectCurrentItem,
  selectActiveCollectionType,
  selectActiveCollectionId,
} from "../../core/selectors";
import { createPlaySessionTracker } from "./streamTracker";

export function usePlayerStreamAnalytics(options?: {
  disabled?: boolean;
}): void {
  const player = usePlayerEngine();
  const data = useData();
  const playOutbox = data.reporting;
  const playLifecycle = player.lifecycle;
  const audioPlayer = player.audio;
  const { getConfirmedProgressPositionForQueueItem } = player.progress;
  const playerStore = usePlayerStoreApi();

  const sqliteReady = useAppSetupStore((state) => state.sqliteIsReady);
  const disabled =
    (options?.disabled ?? false) || (Platform.OS !== "web" && !sqliteReady);
  useEffect(() => {
    if (disabled || !data.isCurrent()) return;
    const tracker = createPlaySessionTracker(playOutbox.sink);
    let disposed = false;
    let deviceId: string | null = null;
    let identity = data.auth.currentUser()?.uid || "";
    let identityTimer: ReturnType<typeof setTimeout> | undefined;
    // These run inside native audio events, store updates and track changes.
    // Recording evidence must not break playback (LIS-657). The outbox keeps
    // events it cannot store yet; anything else that throws stops here and is
    // reported once per run of failures, because progress syncs ten times a
    // second. A start that threw leaves no play id, so the next sync retries it.
    let failing = false;
    const recording =
      <Args extends unknown[]>(record: (...args: Args) => void) =>
      (...args: Args) => {
        try {
          record(...args);
          failing = false;
        } catch (error) {
          if (failing) return;
          failing = true;
          captureException(error, {
            channel: "error",
            area: "player",
            operation: "play-evidence-record",
          });
        }
      };
    const sync = recording(() => {
      if (disposed || !data.isCurrent() || !deviceId) return;
      const state = playerStore.getState();
      const item = selectCurrentItem(state);
      const type = selectActiveCollectionType(state);
      const collectionId = selectActiveCollectionId(state);
      const user = getUser(data);
      const country = user?.plan?.countryId || user?.countryCode;
      tracker.setContext(
        item
          ? {
              song_id: item.metadata.id,
              queueItemId: item.qid,
              device_id: deviceId,
              platform:
                Platform.OS === "web"
                  ? "web"
                  : Platform.OS === "ios"
                    ? "ios"
                    : "android",
              collection:
                type && collectionId ? `${type}:${collectionId}` : null,
              ...(country?.length === 2 ? { country_code: country } : {}),
            }
          : null,
      );
      const audio = audioPlayer.getState();
      const matches =
        item &&
        audioPlayer.activeTrackId === item.metadata.id &&
        audioPlayer.activeQueueItemId === item.qid;
      const position = getConfirmedProgressPositionForQueueItem(item?.qid);
      if (matches && audio.isPositionConfirmed && position != null)
        tracker.onProgress(position);
      tracker.setPlayback(
        Boolean(
          matches &&
          position != null &&
          audio.isPositionConfirmed &&
          audio.playbackState === "playing" &&
          state.userIntent === "playing",
        ),
        state.userIntent === "paused",
      );
    });
    const counted = new Set<string>();
    playOutbox.setListeners((id, credentials, acceptedStart) => {
      tracker.onStarted(id, credentials);
      if (
        acceptedStart &&
        data.auth.currentUser() &&
        data.isCurrent() &&
        !counted.has(credentials.playback_id)
      ) {
        counted.add(credentials.playback_id);
        incrementFairPlays(data);
      }
    });
    playOutbox.start();
    const unsubscribeStore = playerStore.subscribe(sync);
    const unsubscribeAudio = audioPlayer.on(sync);
    const unsubscribeSeek = playLifecycle.subscribeSeek(tracker.onSeek);
    const restart = recording(tracker.restart);
    const unsubscribeLifecycle = playLifecycle.subscribe(restart);
    const hide = () => tracker.onPageHide();
    if (Platform.OS === "web" && typeof window !== "undefined")
      window.addEventListener("pagehide", hide);
    const resolveDevice = () => {
      getPlayDeviceId()
        .then((id) => {
          if (disposed) return;
          deviceId = id;
          sync();
        })
        .catch(() => {
          if (!disposed) identityTimer = setTimeout(resolveDevice, 1000);
        });
    };
    const unsubscribeAuth = data.auth.subscribe((user) => {
      if (disposed || !data.isCurrent()) return;
      const next = user?.uid || "";
      if (identity === next) return;
      identity = next;
      restart("user");
      sync();
    });
    resolveDevice();
    const stop = () => {
      if (disposed) return;
      disposed = true;
      clearTimeout(identityTimer);
      unsubscribeAuth();
      unsubscribeStore();
      unsubscribeAudio();
      unsubscribeLifecycle();
      unsubscribeSeek();
      if (Platform.OS === "web" && typeof window !== "undefined") {
        window.removeEventListener("pagehide", hide);
      }
      tracker.dispose();
      data.signal.removeEventListener("abort", stop);
      playOutbox.setListeners();
      playOutbox.stop();
    };
    data.signal.addEventListener("abort", stop, { once: true });
    return stop;
  }, [
    disabled,
    data,
    player,
    audioPlayer,
    getConfirmedProgressPositionForQueueItem,
    playLifecycle,
    playOutbox,
    playerStore,
  ]);
}
